import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";
import type { Permission } from "../../auth/HasPermission";

type PermissionsResponse =
  paths["/permissions"]["get"]["responses"]["200"]["content"]["application/json"];

export type PermissionCatalogEntry = PermissionsResponse["permissions"][number];

type RoleResponse =
  paths["/roles/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения
// и перечень полей, не прошедших проверку.
interface ErrorBody {
  error: string;
  // Отсутствует, когда отказ не связан с полями формы (нет прав, конфликт
  // состояния): подсвечивать в форме тогда нечего.
  fields?: string[];
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; catalog: PermissionCatalogEntry[] }
  | { kind: "error"; message: string };

export interface FieldErrors {
  // Отсутствует, когда к названию роли претензий нет.
  name?: string;
  // Отсутствует, когда к уровню роли претензий нет.
  level?: string;
  // Отсутствует, когда отказ разложен по полям: общий текст нужен только тому
  // отказу, который ни к одному полю не относится.
  general?: string;
}

export interface UseRoleEditorResult {
  mode: "create" | "edit";
  load: LoadState;
  name: string;
  level: number;
  selected: Set<Permission>;
  editable: boolean;
  fieldErrors: FieldErrors;
  submitting: boolean;
  setName: (value: string) => void;
  setLevel: (value: number) => void;
  togglePermission: (permission: Permission) => void;
  submit: () => void;
}

export function useRoleEditor(onSaved: () => void): UseRoleEditorResult {
  const parameters = useParams<{ id: string }>();
  const roleId = parameters.id;
  const mode: "create" | "edit" = roleId === undefined ? "create" : "edit";

  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [name, setName] = useState("");
  const [level, setLevel] = useState(1);
  const [selected, setSelected] = useState<Set<Permission>>(new Set());
  const [editable, setEditable] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const catalog = await fetchCatalog();
        if (roleId !== undefined) {
          const role = await fetchRole(roleId);
          if (cancelled) {
            return;
          }
          setName(role.name);
          setLevel(role.level);
          setSelected(new Set(role.permissions));
          setEditable(role.type === "user");
        }
        if (!cancelled) {
          setLoad({ kind: "ready", catalog });
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to load role editor");
        if (!cancelled) {
          setLoad({ kind: "error", message });
        }
      }
    };

    void loadData();
    return () => {
      cancelled = true;
    };
  }, [roleId]);

  const togglePermission = useCallback((permission: Permission) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  }, []);

  const submit = useCallback(() => {
    void (async () => {
      if (!editable) {
        return;
      }
      setSubmitting(true);
      setFieldErrors({});
      const body = JSON.stringify({ name, level, permissions: Array.from(selected) });
      const target = roleId === undefined ? "/api/roles" : `/api/roles/${roleId}`;
      const method = roleId === undefined ? "POST" : "PUT";
      try {
        const response = await fetchWithRefresh(target, {
          method,
          headers: { "Content-Type": "application/json" },
          body,
        });
        if (!response.ok) {
          const errorBody = (await response.json()) as ErrorBody;
          setFieldErrors(toFieldErrors(response.status, errorBody));
          return;
        }
        onSaved();
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Не удалось сохранить роль";
        logger.error({ err: message }, "Failed to save role");
        setFieldErrors({ general: "Не удалось сохранить роль" });
      } finally {
        setSubmitting(false);
      }
    })();
  }, [name, level, selected, roleId, editable, onSaved]);

  return {
    mode,
    load,
    name,
    level,
    selected,
    editable,
    fieldErrors,
    submitting,
    setName,
    setLevel,
    togglePermission,
    submit,
  };
}

async function fetchCatalog(): Promise<PermissionCatalogEntry[]> {
  const response = await fetchWithRefresh("/api/permissions");
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = (await response.json()) as PermissionsResponse;
  return data.permissions;
}

async function fetchRole(roleId: string): Promise<RoleResponse> {
  const response = await fetchWithRefresh(`/api/roles/${roleId}`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return (await response.json()) as RoleResponse;
}

function toFieldErrors(status: number, body: ErrorBody): FieldErrors {
  let singleResult: FieldErrors;
  if (status === 409) {
    singleResult = { name: "Роль с таким названием уже существует" };
  } else {
    const fields = body.fields === undefined ? [] : body.fields;
    const fieldErrors: FieldErrors = {};
    if (fields.includes("name")) {
      fieldErrors.name = "Укажите название роли";
    }
    if (fields.includes("level")) {
      fieldErrors.level = "Уровень должен быть положительным целым (≥ 1)";
    }
    if (fieldErrors.name === undefined && fieldErrors.level === undefined) {
      fieldErrors.general = "Не удалось сохранить роль";
    }
    singleResult = fieldErrors;
  }
  return singleResult;
}
